/** Hooks de fetch para las tabs del panel contextual de la Vista 360 de compras. */
import { useQuery } from '@tanstack/react-query'
import apiClient from '@/lib/apiClient'

// ─── Tipos de respuesta (espejo de los endpoints en apps.purchases.views) ───────

export interface PurchaseRelatedResponse {
  purchase_order: {
    id: string; order_number: string; status: string; status_display: string; total: string
  } | null
  linked_adjustments: Array<{
    id: string; type: 'NC' | 'ND'; document_type_sii: number; folio: string
    issue_date: string; amount: string; reason: string
  }>
  adjustment_total: string
  adjusted_total: string
  outstanding_amount: string
  adjustment_status: string
  is_adjustment_orphan: boolean
}

export interface PurchaseVoucherResponse {
  accounting_status: string
  voucher: {
    id: string; number: string; status: string; status_display: string
    date: string | null; description: string
    lines: Array<{ account_code: string | null; account_name: string | null; glosa: string; debit: string; credit: string }>
  } | null
}

export interface PurchasePaymentRow {
  id: string; amount: string; currency: string; exchange_rate: string | null
  payment_date: string | null; payment_method: string; payment_method_display: string
  reference: string; status: string; status_display: string; bank_account: string | null
  accounting_status: string; voucher_id: string | null; voucher_number: string | null
}

export interface PurchasePaymentsResponse {
  payment_status: string
  payment_status_display: string
  total: string
  adjusted_total: string
  outstanding_amount: string
  due_date: string | null
  payments: PurchasePaymentRow[]
}

export interface PurchaseSiiAcuse {
  inbound_dte_id: string; forma_pago: number | null; is_credit: boolean; dte_status: string
  accepted: boolean; rejected: boolean; sii_event_registered: boolean
  has_erm: boolean; has_acd: boolean; has_reclamo: boolean
  can_acuse: boolean; can_accept: boolean; can_reject: boolean
}

export interface PurchaseReceptionResponse {
  source: string
  source_display: string
  document_type_sii: number | null
  inbound_dte: {
    id: string; source: string; source_display: string; tipo_dte: number | null; folio: number | null
    status: string; status_display: string; forma_pago: number | null; forma_pago_display: string | null
    received_at: string | null; xml_valid: boolean | null; signature_valid: boolean | null; ted_valid: boolean | null
  } | null
  sii_acuse: PurchaseSiiAcuse | null
  sii_events: Array<{
    id: string; tipo: string; tipo_display: string
    sii_ok: boolean; sii_codigo: string; sii_glosa: string
    registered_at: string | null; registered_by_name: string | null
    sii_fecha_evento: string; rut_responsable: string
    signed: boolean; sent_to_emisor_at: string | null; sent_to_emisor: string
    /** Lo que declaro quien hizo la accion. `recinto` viajo al SII en el
     *  `<Recinto>` del EnvioRecibos; `observaciones` no, pero explica la
     *  decision — que es lo que se necesita al auditar. */
    recinto: string; observaciones: string
  }>
  rcv: {
    periodo: string | null; estado: string | null
    fecha_recepcion: string | null; fecha_acuse: string | null; fecha_reclamo: string | null
    deadline: string | null; days_until_deadline: number | null; xml_mismatch: boolean
  }
}

export interface SupplierContextResponse {
  id: string; legal_name: string; trade_name: string; tax_id: string
  is_international: boolean; country: string; giro: string
  email: string; phone: string; contact_name: string; default_currency: string
  default_expense_account: string | null; default_payable_account: string | null
  financial: { por_pagar: string; vencido: string }
  recent_invoices: Array<{
    id: string; invoice_number: string; issue_date: string | null
    total: string; payment_status: string; payment_status_display: string
  }>
}

// ─── Hooks ────────────────────────────────────────────────────────────────────

function useInvoice360Query<T>(key: string, invId: string, path: string, enabled: boolean) {
  return useQuery<T>({
    queryKey: ['inv360', key, invId],
    queryFn: async () => (await apiClient.get(`/purchases/invoices/${invId}/${path}/`)).data,
    enabled: enabled && !!invId,
  })
}

export const useInvoiceRelated = (invId: string, enabled: boolean) =>
  useInvoice360Query<PurchaseRelatedResponse>('related', invId, 'related', enabled)

export const useInvoiceVoucher = (invId: string, enabled: boolean) =>
  useInvoice360Query<PurchaseVoucherResponse>('voucher', invId, 'accounting-voucher', enabled)

export const useInvoicePayments = (invId: string, enabled: boolean) =>
  useInvoice360Query<PurchasePaymentsResponse>('payments', invId, 'payments', enabled)

export const useInvoiceReception = (invId: string, enabled: boolean) =>
  useInvoice360Query<PurchaseReceptionResponse>('reception', invId, 'reception', enabled)

export const useSupplierContext = (supplierId: string | undefined, enabled: boolean) =>
  useQuery<SupplierContextResponse>({
    queryKey: ['inv360', 'supplier-context', supplierId],
    queryFn: async () => (await apiClient.get(`/purchases/suppliers/${supplierId}/context/`)).data,
    enabled: enabled && !!supplierId,
  })
